import moment from "moment";

import { BillingForm } from "@/Billing/Components/PrivateBilling/BillingForm";
import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { EnvironmentTax } from "@/Common/Helpers/EnvironmentTax";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PrivateBill_BilledTo, RedirectTo } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { TransactionsInboxTableItem } from "../../Models/UI/BillingInboxTableItem";
import { ITransactionsInboxModel } from "../../Models/UI/ITransactionsInboxModel";
import { TransactionsSettingsContainer } from "./TransactionsSettingsContainer";

import "./TransactionsInbox.css";

interface ITransactionsInboxProps {
    Model: ITransactionsInboxModel;
}

class TransactionsInbox extends React.Component<ITransactionsInboxProps, never> {
    private helper: MyleHelper = new MyleHelper();

    public render(): JSX.Element {
        const saveOptionKey = "TransactionsInboxSettings";

        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(saveOptionKey, this.GetMyleTableColumnsDefaultOptions());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);

        var cfgm: ColumnConfigurationModel[] = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        return (
            <div className="workspace transactions-inbox">
                {this.props.Model.get("BillingPopupIsVisible") === true && <BillingForm model={billingStore.BillingPopupModel} />}
                <CommonTableHeaderComponent
                    title={cacheStore.GetString("MyBillingLabel")}
                    searchText={this.props.Model.get("GlobalTransactionsSearchText")}
                    searchPlaceholder={this.props.Model.get("GlobalTransactionsSearchPlaceholder")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.Billing.Transactions_SearchBoxTextChanged, { value: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Billing.Transactions_SearchBoxTextChanged)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Billing.Transactions_SearchSettings_ShowOptionsButtonClick)}
                    onPrintClick={async () => await this.OnPrintReport(tableColumns)}
                />

                <div id="posref" className="tb ws-scrollable">
                    {this.props.Model.get("ShowTransactionsDisplaySettings")
                        && <TransactionsSettingsContainer className="filter-component transaction" Model={this.props.Model} />
                    }

                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="NotesInbox_main_tb"
                            Columns={tableColumns}
                            DataSource={this.props.Model.get("TableData")}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickOperation={LocalOperations.Billing.Transactions_Open_BillingPopup}
                            SaveOptionskeyTarget={saveOptionKey}
                        />
                    </div>
                </div>
                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-4 col-md-4 col-sm-4">
                            <SimplePagerControl
                                PageSize={this.props.Model.get("SelectedPageSizeOption")}
                                TotalRows={this.props.Model.get("TotalQueryRows")}
                                CurrentPage={this.props.Model.get("CurrentPage")}
                                EventToDispatchOnPageChange={LocalOperations.Billing.Transactions_Search_CurrentPageChange}
                            />
                        </div>
                        <div className="col-lg-8 col-md-8 col-sm-8 text-right">
                            <p className="billing footer-text">
                                <strong>{cacheStore.GetString("SubtotalLabel")}: ${this.props.Model.get("SubtotalSum")} </strong>
                                <strong>{cacheStore.GetString("DiscountLabel")}: ${this.props.Model.get("DiscountSum")} </strong>
                                {this.shouldShowGst() && <strong> {cacheStore.GetString("GstMLabel")}: ${this.props.Model.get("GSTSum")} </strong>}
                                {this.shouldShowPstOrQst() && <strong> {EnvironmentTax.getPstOrQstLabel()}: ${this.props.Model.get("QSTSum")} </strong>}
                                {this.shouldShowHst() && <strong>{cacheStore.GetString("HstMLabel")}: ${this.props.Model.get("HSTSum")} </strong>}
                                {this.shouldShowGenericTax() && <strong>{cacheStore.GetString("TaxLabel")}: ${this.props.Model.get("GenericTaxSum")} </strong>}
                                <strong>{cacheStore.GetString("TotalLabel")}: ${this.props.Model.get("TotalBillSum")}</strong>
                            </p>
                        </div>
                    </div>
                </div>
                {this.props.Model.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={cfgm}
                        SaveOptionskeyTarget={saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.Billing.Transactions_SearchSettings_ColumnOrderingPopup_Close}
                    />
                )}
            </div>
        );
    }

    private GetMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        const columns = [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("CreationDateLabel"),
                FieldName: "TransactionDate",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("NumberLabel"),
                FieldName: "TransactionNumber",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ProviderLabel"),
                FieldName: "TransactionProvider",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SiteInfoLabel"),
                FieldName: "SiteName",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientLabel"),
                FieldName: "PatientName",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: sessionStore.getDefaultHealthCardLabel(),
                FieldName: "NAM",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PaymentMethodLabel"),
                FieldName: "PaymentMethod",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("BilledByLabel"),
                FieldName: "BilledBy",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SubtotalLabel"),
                FieldName: "Subtotal",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DiscountLabel"),
                FieldName: "Discount",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("TotalLabel"),
                FieldName: "Total",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("VisitDateLabel"),
                FieldName: "VisitDate",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            })
        ];

        if (cacheStore.ClinicSettings.EnablePrivateBillRecipient) {
            columns.push(new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("BilledToLabel"),
                FieldName: "BilledTo",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }));

            columns.push(new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PaymentDateLabel"),
                FieldName: "PaymentDate",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }));
        }

        if (this.shouldShowGst()) {
            columns.push(new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("GstMLabel"),
                FieldName: "GST",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }));
        }

        if (this.shouldShowPstOrQst()) {
            columns.push(new MyleTableColumnOptions({
                HeaderText: EnvironmentTax.getPstOrQstLabel(),
                FieldName: "QST",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            }));
        }

        if (this.shouldShowHst()) {
            columns.push(
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("HstMLabel"),
                    FieldName: "HST",
                    WidthPercentageDefault: 6,
                    RowClickEnabled: true
                })
            );
        }

        if (this.shouldShowGenericTax()) {
            columns.push(
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("TaxLabel"),
                    FieldName: "GenericTax",
                    WidthPercentageDefault: 6,
                    RowClickEnabled: true
                })
            );
        }

        return columns;
    }

    private getTableColumns(columnOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<TransactionsInboxTableItem>(
            columnOptions,
            column => {
                switch (column.FieldName) {
                    case "TransactionDate":
                        return {
                            renderCell: item => {
                                var date = moment(item.Model.TransactionDate).format("YYYY/MM/DD");
                                var time = moment(item.Model.TransactionDate).format("HH:mm:ss A");
                                var messageDate = date + " " + cacheStore.GetString("AtLabel") + " " + time;

                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{messageDate}</div>);
                                else
                                    return (<div>{messageDate}</div>);
                            }
                        };

                    case "VisitDate":
                        return {
                            renderCell: item => {
                                var date = moment(item.Model.VisitDate).format("YYYY/MM/DD");

                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{date}</div>);
                                else
                                    return (<div>{date}</div>);
                            }
                        };

                    case "TransactionNumber":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.TransactionNumber}</div>);
                                else
                                    return (<div>{item.Model.TransactionNumber}</div>);
                            }
                        };

                    case "TransactionProvider":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.TransactionProvider}</div>);
                                else
                                    return (<div>{item.Model.TransactionProvider}</div>);
                            }
                        };

                    case "SiteName":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.SiteName}</div>);
                                else
                                    return (<div>{item.Model.SiteName}</div>);
                            }
                        };

                    case "PatientName":
                        return {
                            renderCell: item => (
                                <div><HyperLinkRedirectControl target={RedirectTo.Patient} redirectValue={item.Model.PatientContactId} text={item.Model.PatientName} /></div>
                            )
                        };

                    case "NAM":
                        return {
                            renderCell: item => (
                                <div>{item.Model.patientNAM}</div>
                            )
                        };

                    case "PaymentMethod":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.PaymentMethod}</div>);
                                else
                                    return (<div>{item.Model.PaymentMethod}</div>);
                            }
                        };

                    case "BilledBy":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.BilledBy}</div>);
                                else
                                    return (<div>{item.Model.BilledBy}</div>);
                            }
                        };

                    case "Subtotal":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.SubTotal.toFixed(2)}</div>);
                                else
                                    return (<div>{item.Model.SubTotal.toFixed(2)}</div>);
                            }
                        };

                    case "Discount":
                        return {
                            renderCell: item => {
                                let discount = item.Model.Discount_Dollars;
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{discount.toFixed(2)}</div>);
                                else
                                    return (<div>{discount.toFixed(2)}</div>);
                            }
                        };

                    case "BilledTo":
                        return {
                            renderCell: item => {
                                if (!cacheStore.ClinicSettings.EnablePrivateBillRecipient)
                                    return null;

                                return (
                                    <div style={{ color: item.Model.IsInvalid ? "red" : "" }}>
                                        {(item.Model.BilledTo == PrivateBill_BilledTo.ThirdParty
                                            ? `${item.Model.RecipientName}`
                                            : cacheStore.GetString("PatientLabel"))
                                        }
                                    </div>
                                );
                            }
                        };

                    case "GST":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.GST.toFixed(2)}</div>);
                                else
                                    return (<div>{item.Model.GST.toFixed(2)}</div>);
                            }
                        };

                    case "QST":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.QST.toFixed(2)}</div>);
                                else
                                    return (<div>{item.Model.QST.toFixed(2)}</div>);
                            }
                        };

                    case "HST":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.HST.toFixed(2)}</div>);
                                else
                                    return (<div>{item.Model.HST.toFixed(2)}</div>);
                            }
                        };

                    case "GenericTax":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.GenericTax.toFixed(2)}</div>);
                                else
                                    return (<div>{item.Model.GenericTax.toFixed(2)}</div>);
                            }
                        };

                    case "PaymentDate":
                        return {
                            renderCell: item => {
                                if (!cacheStore.ClinicSettings.EnablePrivateBillRecipient)
                                    return null;

                                const date = item.Model.PaymentDate ? moment(item.Model.PaymentDate).format("YYYY/MM/DD") : "";

                                return (
                                    <div style={{ color: item.Model.IsInvalid ? "red" : "" }}>
                                        {date}
                                    </div>
                                );
                            }
                        };

                    case "Total":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.TotalBill.toFixed(2)}</div>);
                                else
                                    return (<div>{item.Model.TotalBill.toFixed(2)}</div>);
                            }
                        };
                }
            },
            false
        );
    }

    public loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        // Get The Render Options
        var defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };
        return Immutable.fromJS(defaultTablePluginOptions);
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public componentDidUpdate(): void {
        $(window).trigger("resize");
    }

    private async OnPrintReport(tableColumns: Immutable.List<MyleTableColumn>): Promise<void> {
        const columnsToShow = tableColumns.filter(p => p.columnOptions.IsHidden === false).map(p => p.columnOptions.FieldName).toArray();
        await billingStore.BillingAction.PrintTransactionsReport(billingStore.TransactionsInboxModel, columnsToShow);
    }

    private shouldShowGst(): boolean {
        return this.props.Model.get("GSTSum") > 0
            || EnvironmentTax.hasGst();
    }

    private shouldShowPstOrQst(): boolean {
        return this.props.Model.get("QSTSum") > 0
            || EnvironmentTax.hasPstOrQst();
    }

    private shouldShowHst(): boolean {
        return this.props.Model.get("HSTSum") > 0
            || EnvironmentTax.hasHst();
    }

    private shouldShowGenericTax(): boolean {
        return this.props.Model.get("GenericTaxSum") > 0
            || EnvironmentTax.hasGenericTax();
    }
}

export { TransactionsInbox };
